import * as THREE from 'three';
import { CollabCursorData, COLLAB_CONFIG } from './fiko-collab-types';

/**
 * FikoCollabCursor - 3D visualization of another user's cursor
 *
 * Displays:
 * - A fixed-size ArrowHelper showing cursor direction
 * - A sphere at the intersection point (if any)
 * - A label with the user's name attached to the arrow
 */
export class FikoCollabCursor extends THREE.Group {
    readonly type = 'FikoCollabCursor';

    private userId: string;
    private userName: string;
    private userColor: string;

    // 3D elements
    private arrow: THREE.ArrowHelper;
    private pointer: THREE.Mesh;
    private label: THREE.Sprite;

    // Interpolation targets
    private targetRayOrigin = new THREE.Vector3();
    private targetRayDirection = new THREE.Vector3();
    private targetWorldPosition = new THREE.Vector3();
    private hasWorldPosition = false;

    // Fixed arrow size (large enough to be visible at any scale)
    private static readonly ARROW_LENGTH = 50.0;
    private static readonly ARROW_HEAD_LENGTH = 15.0;
    private static readonly ARROW_HEAD_WIDTH = 10.0;

    // ⚡ Shared geometry - same for all cursors
    private static readonly SHARED_POINTER_GEOMETRY = new THREE.SphereGeometry(5, 16, 16);

    // Pre-allocated buffers
    private static _tempVec = new THREE.Vector3();

    constructor(userId: string, userName: string, userColor: string) {
        super();

        this.userId = userId;
        this.userName = userName;
        this.userColor = userColor || '#00FFFF';

        this.name = `CollabCursor_${userId}`;

        // Create arrow helper
        this.arrow = this.createArrow();
        this.add(this.arrow);

        // Create pointer sphere
        this.pointer = this.createPointer();
        this.add(this.pointer);

        // Create name label
        this.label = this.createLabel();
        this.add(this.label);

        // Initially hidden
        this.visible = false;
    }

    /**
     * Create the arrow visualization (fixed size, always visible)
     */
    private createArrow(): THREE.ArrowHelper {
        const dir = new THREE.Vector3(0, -1, 0);
        const origin = new THREE.Vector3(0, 0, 0);
        const color = new THREE.Color(this.userColor);

        const arrow = new THREE.ArrowHelper(
            dir,
            origin,
            FikoCollabCursor.ARROW_LENGTH,
            color,
            FikoCollabCursor.ARROW_HEAD_LENGTH,
            FikoCollabCursor.ARROW_HEAD_WIDTH
        );

        // Make arrow always visible (no depth test)
        arrow.traverse((child) => {
            if (child instanceof THREE.Line || child instanceof THREE.Mesh) {
                const mat = child.material as THREE.Material;
                mat.depthTest = false;
                mat.depthWrite = false;
                mat.transparent = true;
                (mat as any).opacity = 0.9;
            }
            child.renderOrder = 9999;
            child.frustumCulled = false;
        });

        return arrow;
    }

    /**
     * Create the pointer sphere at intersection point (larger for visibility)
     */
    private createPointer(): THREE.Mesh {
        // ⚡ Use shared geometry to avoid per-cursor allocation
        const geometry = FikoCollabCursor.SHARED_POINTER_GEOMETRY;
        const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.userColor),
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.renderOrder = 10000;
        mesh.frustumCulled = false;
        return mesh;
    }

    /**
     * Create the username label sprite (attached to arrow)
     */
    private createLabel(): THREE.Sprite {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = 256;
        canvas.height = 64;

        // Draw background with outline
        ctx.fillStyle = this.userColor;
        ctx.globalAlpha = 0.9;
        ctx.roundRect(0, 0, canvas.width, canvas.height, 8);
        ctx.fill();

        // Draw border
        ctx.globalAlpha = 1;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 3;
        ctx.roundRect(0, 0, canvas.width, canvas.height, 8);
        ctx.stroke();

        // Draw text
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.userName, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(30, 7.5, 1);  // Much larger for visibility
        sprite.renderOrder = 10001;
        sprite.center.set(0.5, 0.5);
        return sprite;
    }

    /**
     * Update cursor from server data
     */
    updateFromData(data: CollabCursorData): void {
        // Update targets for interpolation
        this.targetRayOrigin.set(
            data.rayOrigin[0],
            data.rayOrigin[1],
            data.rayOrigin[2]
        );

        this.targetRayDirection.set(
            data.rayDirection[0],
            data.rayDirection[1],
            data.rayDirection[2]
        ).normalize();

        if (data.worldPosition) {
            this.targetWorldPosition.set(
                data.worldPosition[0],
                data.worldPosition[1],
                data.worldPosition[2]
            );
            this.hasWorldPosition = true;
        } else {
            this.hasWorldPosition = false;
        }

        // Apply immediately (or could be lerped in animation loop)
        this.applyPositions();
    }

    /**
     * Apply current target positions to 3D elements
     */
    private applyPositions(): void {
        // Calculate cursor position - either at intersection or along ray
        let cursorPos: THREE.Vector3;

        if (this.hasWorldPosition) {
            cursorPos = this.targetWorldPosition.clone();
            console.log('[CollabCursor] Using world position:', cursorPos.toArray());
        } else {
            // Show cursor at a fixed distance along ray (100 units from origin for visibility)
            cursorPos = FikoCollabCursor._tempVec
                .copy(this.targetRayDirection)
                .multiplyScalar(100)
                .add(this.targetRayOrigin)
                .clone();
            console.log('[CollabCursor] Using ray position:', cursorPos.toArray(), 'origin:', this.targetRayOrigin.toArray());
        }

        // Position arrow at cursor location, pointing in ray direction
        this.arrow.position.copy(cursorPos);
        this.arrow.setDirection(this.targetRayDirection);

        // Position pointer sphere at cursor position
        this.pointer.position.copy(cursorPos);
        this.pointer.visible = true;

        // Scale pointer based on whether we hit something
        const scale = this.hasWorldPosition ? 1.0 : 0.6;
        this.pointer.scale.setScalar(scale);

        // Position label above the cursor
        this.label.position.copy(cursorPos);
        this.label.position.y += 20; // Offset above cursor

        console.log('[CollabCursor] Applied positions - arrow:', this.arrow.position.toArray(), 'visible:', this.visible);
    }

    /**
     * Update with lerp interpolation (call in animation loop for smooth movement)
     */
    lerpUpdate(deltaTime: number = 0.016): void {
        // Lerp positions for smooth movement
        const lerpFactor = Math.min(1, COLLAB_CONFIG.CURSOR_LERP * deltaTime * 60);

        // This would require storing current positions separately
        // For now, we apply immediately in updateFromData
    }

    /**
     * Update color
     */
    setColor(color: string): void {
        this.userColor = color;
        const threeColor = new THREE.Color(color);

        // Update arrow color
        this.arrow.setColor(threeColor);

        // Update pointer color
        (this.pointer.material as THREE.MeshBasicMaterial).color = threeColor;

        // Recreate label with new color
        const oldLabel = this.label;
        this.remove(oldLabel);
        oldLabel.material.map?.dispose();
        oldLabel.material.dispose();

        this.label = this.createLabel();
        this.add(this.label);
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        // Arrow helper
        this.arrow.dispose();

        // Pointer
        this.pointer.geometry.dispose();
        (this.pointer.material as THREE.Material).dispose();

        // Label
        this.label.material.map?.dispose();
        this.label.material.dispose();
    }
}
